import type {FormEvent} from "react";
import {useState} from "react";
import {BaseAlert, BaseButton, BaseInput} from "@/components";
import {recoverPasswordSchema} from "./recoverPassword.schema";
import {useRecoverPassword} from "./useRecoverPassword";

export interface RecoverPasswordPageProps {
  onBack?: () => void;
}

const RecoverPasswordPage = ({onBack}: RecoverPasswordPageProps) => {
  const {recover, isPending, isError, isSuccess, error} = useRecoverPassword();
  const [email, setEmail] = useState("");
  const [fieldError, setFieldError] = useState<string | undefined>();

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const result = recoverPasswordSchema.safeParse({email});
    if (!result.success) {
      setFieldError(result.error.issues[0]?.message ?? "Correo inválido");
      return;
    }
    setFieldError(undefined);
    recover(result.data.email);
  };

  return (
    <div className="mx-auto flex w-full max-w-[420px] flex-col gap-6 rounded-[var(--radius-xl)] bg-[var(--color-surface)] p-8 shadow-[var(--shadow-lg)]">
      <header className="flex flex-col gap-2 text-center">
        <h1 className="m-0 text-[var(--font-h1)] font-bold text-[var(--color-text)]">Recuperar contraseña</h1>
        <p className="m-0 text-[var(--font-body-lg)] text-[var(--color-text-secondary)]">
          Te enviaremos un enlace a tu correo para restablecerla
        </p>
      </header>

      {isSuccess ? (
        <>
          <BaseAlert
            variant="success"
            title="Revisa tu correo"
          >
            Si el correo está registrado, recibirás un enlace para restablecer tu contraseña.
          </BaseAlert>
          {onBack && (
            <BaseButton
              type="button"
              variant="ghost"
              size="md"
              fullWidth
              onClick={onBack}
            >
              Volver al inicio de sesión
            </BaseButton>
          )}
        </>
      ) : (
        <form
          onSubmit={handleSubmit}
          className="flex flex-col gap-4"
          noValidate
        >
          <BaseInput
            type="email"
            label="Correo electrónico"
            placeholder="tu@correo.com"
            autoComplete="email"
            value={email}
            onChange={event => {
              setEmail(event.target.value);
              if (fieldError) setFieldError(undefined);
            }}
            error={fieldError}
            fullWidth
            required
          />

          {isError && <BaseAlert variant="danger">{error?.message ?? "No se pudo procesar la solicitud"}</BaseAlert>}

          <BaseButton
            type="submit"
            variant="primary"
            size="lg"
            loading={isPending}
            fullWidth
          >
            Enviar enlace
          </BaseButton>

          {onBack && (
            <BaseButton
              type="button"
              variant="ghost"
              size="md"
              fullWidth
              onClick={onBack}
            >
              Volver
            </BaseButton>
          )}
        </form>
      )}
    </div>
  );
};

export default RecoverPasswordPage;
